/**
 * Calendar-date and timezone maths on top of Intl (spec03 FR15). Dates are ISO strings
 * (YYYY-MM-DD) without a time or zone; instants are JS Dates (UTC).
 */

const DAY_MS = 86_400_000;

export type WeekStart = "monday" | "sunday";

/** Day number since 1970-01-01, so date maths is integer maths. */
function dayNumber(date: string): number {
  const [y = 1970, m = 1, d = 1] = date.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

function fromDayNumber(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

export function isoDate(year: number, month: number, day: number): string {
  return fromDayNumber(Math.round(Date.UTC(year, month - 1, day) / DAY_MS));
}

export function dateParts(date: string): { year: number; month: number; day: number } {
  const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
  return { year, month, day };
}

export const addDays = (date: string, days: number): string =>
  fromDayNumber(dayNumber(date) + days);

/** Whole days from `a` to `b` (positive when `b` is later). */
export const diffDays = (a: string, b: string): number => dayNumber(b) - dayNumber(a);

/** 0 = Sunday … 6 = Saturday. */
export const dayOfWeek = (date: string): number => (((dayNumber(date) + 4) % 7) + 7) % 7;

export const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

export const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

export function startOfWeek(date: string, weekStart: WeekStart): string {
  const offset = (dayOfWeek(date) - (weekStart === "monday" ? 1 : 0) + 7) % 7;
  return addDays(date, -offset);
}

export function startOfMonth(date: string): string {
  const { year, month } = dateParts(date);
  return isoDate(year, month, 1);
}

/** Moves by whole months and clamps the day (31 Jan + 1 month = 28/29 Feb). */
export function addMonths(date: string, months: number): string {
  const { year, month, day } = dateParts(date);
  const index = year * 12 + (month - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return isoDate(y, m, Math.min(day, daysInMonth(y, m)));
}

/** The 42 days (6 weeks) shown for the month of `date`. */
export function monthGrid(date: string, weekStart: WeekStart): string[] {
  const first = startOfWeek(startOfMonth(date), weekStart);
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

/** Every date from `from` to `to`, inclusive. */
export function eachDay(from: string, to: string): string[] {
  const length = diffDays(from, to) + 1;
  return Array.from({ length: Math.max(0, length) }, (_, i) => addDays(from, i));
}

// --- Timezones ---

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

function wallClock(instantMs: number, timeZone: string) {
  const parts: Record<string, string> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(instantMs))) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** UTC offset of `timeZone` at an instant, in ms (Vienna in summer: +2 h). */
function offsetAt(instantMs: number, timeZone: string): number {
  const w = wallClock(instantMs, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(instantMs / 1000) * 1000;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Local date, time (HH:MM) and minute of the day of an instant in a timezone. */
export function zonedParts(instant: Date, timeZone: string) {
  const w = wallClock(instant.getTime(), timeZone);
  return {
    date: `${w.year}-${pad2(w.month)}-${pad2(w.day)}`,
    time: `${pad2(w.hour)}:${pad2(w.minute)}`,
    minutes: w.hour * 60 + w.minute,
  };
}

/**
 * The instant of a local wall time in a timezone. A time that does not exist (spring
 * forward gap) moves forward by the gap (02:30 → 03:30); a time that exists twice (fall
 * back) resolves to the earlier instant, like most calendar apps.
 */
export function zonedToInstant(date: string, time: string, timeZone: string): Date {
  const { year, month, day } = dateParts(date);
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  // At most one transition lies within a day, so the offsets a day before and after
  // cover both sides of it.
  const offsets = [offsetAt(wall - DAY_MS, timeZone), offsetAt(wall + DAY_MS, timeZone)];
  const valid = offsets.map((o) => wall - o).filter((c) => offsetAt(c, timeZone) === wall - c);
  if (valid.length > 0) return new Date(Math.min(...valid));
  return new Date(wall - (offsets[0] ?? 0));
}

/** The instant a local day starts (usually 00:00). */
export const startOfDayInstant = (date: string, timeZone: string): Date =>
  zonedToInstant(date, "00:00", timeZone);
